import React, { useState, useEffect } from "react";
import { googleReviews, restaurantInfo } from "../data/mock";
import { Star, ChevronLeft, ChevronRight, ExternalLink } from "lucide-react";

export default function GoogleReviews() {
  const [index, setIndex] = useState(0);
  const count = googleReviews.length;

  useEffect(() => {
    const t = setInterval(() => setIndex((i) => (i + 1) % count), 6000);
    return () => clearInterval(t);
  }, [count]);

  const next = () => setIndex((i) => (i + 1) % count);
  const prev = () => setIndex((i) => (i - 1 + count) % count);
  const review = googleReviews[index];

  return (
    <section id="reviews" className="py-24 md:py-32 bg-[var(--mm-bg-alt)] border-t border-b border-[var(--mm-line)]">
      <div className="max-w-[1000px] mx-auto px-6 md:px-12">
        <div className="text-center mb-12 fade-in">
          <div className="section-label mb-4">Reviews</div>
          <h2 className="font-display text-5xl md:text-6xl italic font-light text-[var(--mm-cream)] leading-[0.95]">
            Featured on <span className="text-[var(--mm-accent)]">Google.</span>
          </h2>
          <div className="flex items-center justify-center gap-2 mt-6">
            {[...Array(5)].map((_, i) => (
              <Star key={`overall-star-${i}`} size={16} className="fill-[var(--mm-accent)] text-[var(--mm-accent)]" />
            ))}
            <span className="font-display text-2xl text-[var(--mm-cream)] ml-2">{restaurantInfo.rating.google.score}</span>
            <span className="text-xs tracking-[0.24em] uppercase text-[var(--mm-cream-muted)] ml-1">
              from {restaurantInfo.rating.google.count} reviews
            </span>
          </div>
        </div>

        <div className="relative min-h-[240px] flex items-center fade-in">
          <button
            onClick={prev}
            aria-label="Previous review"
            className="absolute left-0 top-1/2 -translate-y-1/2 w-10 h-10 flex items-center justify-center border border-[var(--mm-line)] text-[var(--mm-cream)] hover:border-[var(--mm-accent)] hover:text-[var(--mm-accent)] transition-colors z-10"
          >
            <ChevronLeft size={16} />
          </button>

          <div className="w-full px-14 md:px-20 text-center">
            <div className="flex justify-center gap-1 mb-6">
              {[...Array(review.rating)].map((_, i) => (
                <Star key={`${review.name}-star-${i}`} size={14} className="fill-[var(--mm-accent)] text-[var(--mm-accent)]" />
              ))}
            </div>
            <blockquote className="font-display italic text-2xl md:text-3xl text-[var(--mm-cream)] leading-relaxed font-light">
              “{review.text}”
            </blockquote>
            <div className="mt-6">
              <div className="text-[var(--mm-accent)] text-sm tracking-[0.2em] uppercase">{review.name}</div>
              <div className="text-[var(--mm-cream-muted)] text-xs mt-1">{review.date} · Google Review</div>
            </div>
          </div>

          <button
            onClick={next}
            aria-label="Next review"
            className="absolute right-0 top-1/2 -translate-y-1/2 w-10 h-10 flex items-center justify-center border border-[var(--mm-line)] text-[var(--mm-cream)] hover:border-[var(--mm-accent)] hover:text-[var(--mm-accent)] transition-colors z-10"
          >
            <ChevronRight size={16} />
          </button>
        </div>

        {/* Dots */}
        <div className="flex justify-center gap-2 mt-10">
          {googleReviews.map((r, i) => (
            <button
              key={r.name || `review-dot-${i}`}
              onClick={() => setIndex(i)}
              aria-label={`Go to review ${i + 1}`}
              className={`h-1 transition-all duration-500 ${
                i === index ? "w-8 bg-[var(--mm-accent)]" : "w-2 bg-[var(--mm-cream-muted)]/40"
              }`}
            />
          ))}
        </div>

        <div className="text-center mt-10 fade-in">
          <a
            href={restaurantInfo.social.google}
            target="_blank"
            rel="noreferrer"
            className="link-underline text-[var(--mm-accent)] text-xs tracking-[0.28em] uppercase inline-flex items-center gap-2"
          >
            Read all reviews on Google <ExternalLink size={12} />
          </a>
        </div>
      </div>
    </section>
  );
}
